// Path builders for Pashmak's art, ported 1:1 from the SwiftUI Shapes in
// ios/FarsiTutor/Features/Character (PashmakView.swift, OutfitArt.swift) and
// App/Theme.swift. Coordinates are the iOS design space: a 260×260 canvas with
// (0,0) at its center; every shape is given its frame's center + size, like a
// SwiftUI `.frame(width:height:).offset(x:y:)`.

/** Colors of the art itself (fixed: the cat looks the same in light and dark). */
export const C = {
  fur: "#FFF6E6",
  furShade: "#F4DFC2",
  outline: "#E2C59C",
  ginger: "#E9B77F",
  earPink: "#F6B1B8",
  eyeDark: "#2E2230",
  firuze: "#1BA5A8",
  firuzeLight: "#7FD6D1",
  lapis: "#1F3C88",
  saffron: "#F2A007",
  gold: "#D4A94F",
  pomegranate: "#C0283B",
  pistachio: "#93C572",
} as const;

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Maps fractional (0…1) coordinates of a w×h frame centered on (cx, cy) to "x y". */
function frame(w: number, h: number, cx: number, cy: number) {
  const x0 = cx - w / 2;
  const y0 = cy - h / 2;
  return (fx: number, fy: number, dx = 0, dy = 0) => `${r2(x0 + fx * w + dx)} ${r2(y0 + fy * h + dy)}`;
}

/** Khatam (8-point Persian star). */
export function khatam(cx: number, cy: number, size: number, points = 8, inner = 0.55): string {
  const r = size / 2;
  let d = "";
  for (let i = 0; i < points * 2; i++) {
    const a = (i * Math.PI) / points - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * inner;
    d += `${i ? "L" : "M"}${r2(cx + Math.cos(a) * rr)} ${r2(cy + Math.sin(a) * rr)}`;
  }
  return d + "Z";
}

/** Boteh jegheh, the Persian paisley. */
export function boteh(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0.85, 0)}C${P(-0.2, 0.3)} ${P(0, 1)} ${P(0.5, 1)}C${P(1.2, 1)} ${P(1.1, 0.4)} ${P(0.85, 0)}Z`;
}

/** A cat ear in a 42×46 frame, drawn with the origin at the middle of its base (its pivot). */
export function ear(w = 42, h = 46): string {
  const P = frame(w, h, 0, -h / 2);
  return `M${P(0, 1)}Q${P(0.1, 0.2)} ${P(0.6, 0)}Q${P(1, 0.5)} ${P(1, 1)}Z`;
}

/** ^ (happy eye) arc. */
export function happyEye(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0, 1)}Q${P(0.5, -0.8)} ${P(1, 1)}`;
}

/** The classic cat "ω" mouth. */
export function catSmile(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0, 0)}Q${P(0.25, 1.4)} ${P(0.5, 0)}Q${P(0.75, 1.4)} ${P(1, 0)}`;
}

export function roundedTriangle(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0, 0, 2)}L${P(1, 0, -2)}Q${P(1, 0.5)} ${P(0.5, 1)}Q${P(0, 0.5)} ${P(0, 0, 2)}Z`;
}

export function drop(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0.5, 0)}Q${P(1.4, 1)} ${P(0.5, 1)}Q${P(-0.4, 1)} ${P(0.5, 0)}Z`;
}

export function dome(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0, 1)}C${P(0, -0.2)} ${P(1, -0.2)} ${P(1, 1)}Z`;
}

export function tapered(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0, 1)}L${P(0.12, 0, 0, 8)}Q${P(0.5, 0, 0, -4)} ${P(0.88, 0, 0, 8)}L${P(1, 1)}Z`;
}

export function crownShape(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  const peaks = 5;
  let d = `M${P(0, 1)}L${P(0, 0.25)}`;
  for (let i = 0; i < peaks; i++) {
    if (i > 0) d += `L${P(i / peaks, 0.45)}`;
    d += `L${P((i + 0.5) / peaks, 0)}`;
    d += `L${P((i + 1) / peaks, i === peaks - 1 ? 0.25 : 0.45)}`;
  }
  return d + `L${P(1, 1)}Z`;
}

export function tulipGlass(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0, 0, 2)}L${P(1, 0, -2)}C${P(0.5, 0.5, 2, -4)} ${P(1, 1, 2, -10)} ${P(1, 1, -4)}L${P(0, 1, 4)}C${P(0, 1, -2, -10)} ${P(0.5, 0.5, -2, -4)} ${P(0, 0, 2)}`;
}

export function steam(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0.5, 1)}C${P(0, 0.7)} ${P(1, 0.3)} ${P(0.5, 0)}`;
}

export function triangle(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0.5, 0)}L${P(1, 1)}L${P(0, 1)}Z`;
}

export function trapezoid(w: number, h: number, cx = 0, cy = 0): string {
  const P = frame(w, h, cx, cy);
  return `M${P(0.18, 0)}L${P(0.82, 0)}L${P(1, 1)}L${P(0, 1)}Z`;
}

/** A 30×30 crescent moon (a circle minus the same circle shifted up-right), centered on (cx, cy). */
export function crescent(cx: number, cy: number): string {
  // Circles of r=15 at (0,0) and (9,-3) meet at (0,-15) and (9,12).
  return `M${r2(cx)} ${r2(cy - 15)}A15 15 0 1 0 ${r2(cx + 9)} ${r2(cy + 12)}A15 15 0 0 1 ${r2(cx)} ${r2(cy - 15)}Z`;
}

/** Arc of a circle from fraction `from` to `to` (SwiftUI `.trim`, 0 = 3 o'clock, clockwise). */
export function arc(r: number, from: number, to: number): string {
  const a0 = from * 2 * Math.PI;
  const a1 = to * 2 * Math.PI;
  const large = to - from > 0.5 ? 1 : 0;
  return `M${r2(Math.cos(a0) * r)} ${r2(Math.sin(a0) * r)}A${r} ${r} 0 ${large} 1 ${r2(Math.cos(a1) * r)} ${r2(Math.sin(a1) * r)}`;
}

/** The halo's rings of small khatam stars (3 rings: 8, 16, 24 stars), as one path. */
export const HALO_PATTERN: string = (() => {
  let d = "";
  for (let ring = 1; ring <= 3; ring++) {
    const r = ring * 36;
    const count = ring * 8;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * 2 * Math.PI;
      d += khatam(Math.cos(a) * r, Math.sin(a) * r, 16);
    }
  }
  return d;
})();
